import React, { MouseEvent, useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { PreviewText } from "@highnote/preview-text/src";
import { Track } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { EntityRowConfig } from "App/components/EntityTable";
import { ENTITY_TYPE } from "App/components/EntityTable/config";
import { EntityIconPlayable } from "App/components/EntityTable/EntityIcon";
import { useSpaces } from "App/components/useEntities";
import { useFiles } from "App/components/useFiles";
import { Button, BUTTON_THEME } from "App/core/Button";
import { PREVIEW_ICON_SIZE, PreviewIcon } from "./PreviewIcon";
import { useSpaceContext } from "./useSpace";
import "./ThumbnailPreview.scss";
import { ViewingSpaceId } from "App/modules/viewingSpace";

/**
 * `tracks` may carry a `viewingSpaceId` (see GlobalAudioPlayer's
 * `QueueableTrack`) — the space each track is being viewed FROM, which for a
 * borrowed track differs from `track.spaceId` (its origin). Artwork is
 * row-authorized, so the fetch has to be made per viewing space rather than
 * as one flat batch: two queued tracks can legitimately belong to two
 * different spaces at the same time.
 */
export const useTrackArtworkUrls = ({
  tracks,
}: {
  // Structurally `QueueableTrack`, spelled inline to avoid importing
  // GlobalAudioPlayer into a common component. Branded so the queue's
  // provenance keeps its guarantee all the way to the file fetch.
  tracks: Array<Track & { viewingSpaceId?: ViewingSpaceId }>;
}) => {
  const { getDownloadUrl } = useFiles();
  const [sources, setSources] = useState<Record<string, string>>({});

  const { entities: spaces } = useSpaces();
  const { space } = useSpaceContext();

  useEffect(() => {
    let unmounted: boolean;
    if (tracks.length === 0) return;

    // Group artwork ids by the VIEWING space of the track that produced
    // them. `""` is the deliberate key for "no viewing space" — a Firestore
    // id is never empty, so it cannot collide with a real one, unlike
    // interpolating `undefined` into the id-shaped string "undefined". Same
    // sentinel convention as the artwork URL cache key in PR #1642.
    // Keyed `ViewingSpaceId | ""` rather than `string`, so the brand
    // survives the grouping and the sentinel stays a distinct literal
    // instead of blurring into any old string.
    const idsBySpace = new Map<ViewingSpaceId | "", Set<string>>();
    tracks.forEach((track) => {
      const id =
        track.artworkFile ||
        spaces.find(({ id: spaceEntityId }) => spaceEntityId === track.spaceId)
          ?.artworkFile ||
        space?.artworkFile;
      if (!id) return;
      const key: ViewingSpaceId | "" = track.viewingSpaceId || "";
      if (!idsBySpace.has(key)) idsBySpace.set(key, new Set());
      idsBySpace.get(key).add(id);
    });
    if (idsBySpace.size === 0) return;

    Promise.all(
      Array.from(idsBySpace.entries()).map(([viewingSpaceId, idSet]) =>
        highnote
          .getFiles({
            ids: Array.from(idSet),
            ...(viewingSpaceId ? { spaceId: viewingSpaceId } : {}),
          })
          .then(async (files) =>
            Object.fromEntries(
              await Promise.all(
                files.map(async (file) => [
                  file.id,
                  await getDownloadUrl(file, viewingSpaceId || undefined).catch(
                    () => "/public/default-track-artwork.png",
                  ),
                ]),
              ),
            ),
          )
          // GET_FILES filters unauthorized ids out of a 200 rather than
          // throwing, so a rejection here is a transport failure, not a
          // denial. One space's failure must not lose the others' results.
          .catch(() => ({}) as Record<string, string>),
      ),
    ).then((perSpace) => {
      if (unmounted) return;
      setSources(Object.assign({}, ...perSpace));
    });

    return () => {
      unmounted = true;
    };
  }, [tracks]);

  return { sources, fallback: "/public/default-track-artwork.png" };
};

/**
 * The singular counterpart to `useTrackArtworkUrls` above, and it needs the
 * same provenance for the same reason.
 *
 * The one caller is `GlobalAudioTrackPreview` (the now-playing bar), which
 * is handed `nowPlaying.track` — a `QueueableTrack` carrying the space it
 * was enqueued FROM. A track's own artwork is TRACK_ATTACHMENT-parented, so
 * the server authorizes it through canViewTrackFromSpace; with no viewing
 * space that resolves to the track's ORIGIN and GET_FILES filters the id
 * out of its 200. Every borrowed track therefore fell back to the generic
 * placeholder in the player bar.
 *
 * Structurally `QueueableTrack`, spelled inline to avoid importing
 * GlobalAudioPlayer into a common component — the same convention
 * `useTrackArtworkUrls` uses.
 */
export const useTrackArtworkUrl = ({
  track,
}: {
  track: Track & { viewingSpaceId?: ViewingSpaceId };
}) => {
  const { getDownloadUrl } = useFiles();
  const [source, setSource] = useState<string>();
  const [fallback, setFallback] = useState<string>(
    "/public/default-track-artwork.png",
  );

  const { entities: spaces } = useSpaces();
  const { space } = useSpaceContext();
  // Off the QUEUED track, not the URL: this bar outlives the route that
  // built the queue.
  const viewingSpaceId = track?.viewingSpaceId;
  const spaceArtworkFile = space?.artworkFile;
  const parentArtworkFile =
    spaces.find((s) => s.id === track?.spaceId)?.artworkFile ||
    spaceArtworkFile;
  const artworkFile = track?.artworkFile || parentArtworkFile;

  useEffect(() => {
    let unmounted: boolean;

    if (!artworkFile) {
      setFallback("/public/default-track-artwork.png");
      return;
    }

    highnote
      .getFiles({
        ids: [artworkFile],
        ...(viewingSpaceId ? { spaceId: viewingSpaceId } : {}),
      })
      .then(async (files) => {
        if (unmounted) return;
        const artworkFileEntity = files.find((f) => f.id === artworkFile);
        const url = await getDownloadUrl(artworkFileEntity, viewingSpaceId);
        setSource(url);
      })
      // getDownloadUrl THROWS on a denial (unlike GET_FILES, which filters),
      // so without this a borrowed track's artwork rejected unhandled.
      .catch(() => {
        if (unmounted) return;
        setSource(undefined);
      });

    return () => {
      unmounted = true;
    };
  }, [artworkFile, viewingSpaceId]);

  return { source, fallback };
};

export const ThumbnailPreview = ({
  className,
  onClick,
  iconSource,
  iconFallback,
  title,
  description,
  row,
  hideThumbnail,
}: {
  className?: string;
  onClick?: () => void;
  iconSource?: string;
  iconFallback: React.ReactNode;
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  row?: EntityRowConfig;
  hideThumbnail?: boolean;
}) => {
  const resizeRef = useRef<HTMLDivElement>();
  const isTitleString = typeof title === "string";

  const handleClick = (e: MouseEvent) => {
    const path = Array.from(e.nativeEvent.composedPath());
    const isFromRow = path.some((el: HTMLElement) =>
      el?.classList?.contains("highnote-thumbnail-preview"),
    );
    if (!isFromRow) return;
    onClick && onClick();
  };

  if (row?.type === ENTITY_TYPE.UPLOAD) {
    return (
      <div
        ref={resizeRef}
        className={classNames(
          "highnote-thumbnail-preview highnote-thumbnail-preview-upload",
          className,
        )}
      >
        <div className="info">
          <div className="thumbnail-preview-image">
            <PreviewIcon
              size={PREVIEW_ICON_SIZE.LARGE}
              src={iconSource}
              fallback={iconFallback}
            />
          </div>

          <span className="title">
            {isTitleString ? (
              <PreviewText watchRefs={[resizeRef]}>{title}</PreviewText>
            ) : (
              title
            )}
          </span>
        </div>
        <span className="description">{description}</span>
      </div>
    );
  }

  return (
    <div
      ref={resizeRef}
      className={classNames("highnote-thumbnail-preview", className)}
    >
      {!hideThumbnail && (
        <div className="thumbnail-preview-track">
          <PreviewIcon
            size={PREVIEW_ICON_SIZE.LARGE}
            src={iconSource}
            fallback={iconFallback}
          />
          {row?.type === ENTITY_TYPE.TRACK && (
            <EntityIconPlayable
              row={row}
              className="thumbnail-preview-playable"
            />
          )}
        </div>
      )}

      <div className="info">
        {onClick ? (
          <Button
            className="title"
            data-is-clickable={!!onClick}
            onClick={handleClick}
            theme={BUTTON_THEME.ICON}
          >
            {isTitleString ? (
              <PreviewText watchRefs={[resizeRef]}>{title}</PreviewText>
            ) : (
              title
            )}
          </Button>
        ) : (
          <span className="title">
            {isTitleString ? (
              <PreviewText watchRefs={[resizeRef]}>{title}</PreviewText>
            ) : (
              title
            )}
          </span>
        )}
        <span className="description">{description}</span>
      </div>
    </div>
  );
};
